import React, { useEffect, useRef } from 'react';
import { Icon } from '@atlassian/learning-components';
import { Link } from 'react-router-dom';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';

import { subnavbarPrimaryItems, subnavbarSecondaryItems } from 'src/pages/utils/navigation';

interface SubNavbarDropdownProps {
  onDropdownItemClick: () => void;
  isMenuOpen: boolean;
  setIsOnDemandMenuOpen: (isOpen: boolean) => void;
}

export const SubNavbarDropdown = ({
  onDropdownItemClick,
  isMenuOpen,
  setIsOnDemandMenuOpen,
}: SubNavbarDropdownProps): React.ReactElement => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleMenu = (): void => {
    setIsOnDemandMenuOpen(!isMenuOpen);
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleMenu();
    }
  };

  useEffect(() => {
    const dropdown = dropdownRef.current;

    if (!dropdown) {
      return;
    }

    const preventScroll = (e: WheelEvent | TouchEvent): void => {
      e.preventDefault();
    };

    dropdown.addEventListener('wheel', preventScroll);
    dropdown.addEventListener('touchmove', preventScroll);

    return () => {
      dropdown.removeEventListener('wheel', preventScroll);
      dropdown.removeEventListener('touchmove', preventScroll);
    };
  }, []);

  return (
    <div css={css({ position: 'relative' })} ref={dropdownRef}>
      <span
        tabIndex={0}
        css={[dropdownTitleStyles, isMenuOpen && css({ color: token('color.link') })]}
        onClick={toggleMenu}
        onKeyDown={handleKeyDown}
      >
        On-demand learning
        <span css={[iconWrapperStyles, isMenuOpen && iconWrapperRotateStyles]}>
          <Icon type="chevron-down" alt={isMenuOpen ? 'Collapse item' : 'Expand item'} />
        </span>
      </span>
      <div aria-hidden={!isMenuOpen} css={[dropdownStyles, isMenuOpen && dropdownOpenStyles]}>
        <ul css={[defaultListStyles, primaryContainerStyles]}>
          {subnavbarPrimaryItems.map(({ title, description, link }) => (
            <li key={title} css={[defaultListItemStyles, primaryItemStyles]}>
              <Link tabIndex={isMenuOpen ? 0 : -1} css={primaryItemLinkStyles} to={link} onClick={onDropdownItemClick}>
                <span css={primaryItemTitleStyles}>{title}</span>
                <span css={primaryItemDescriptionStyles}>{description}</span>
              </Link>
            </li>
          ))}
        </ul>
        <ul css={[defaultListStyles, secondaryContainerStyles]}>
          {subnavbarSecondaryItems.map(({ title, links }) => (
            <li key={title} css={defaultListItemStyles}>
              <span css={secondaryItemTitleStyles}>{title}</span>
              <ul css={[defaultListStyles, secondaryItemListStyles]}>
                {links.map(({ text, href }) => (
                  <li key={text} css={[defaultListItemStyles, secondaryItemLinkWrapperStyles]}>
                    <Link
                      tabIndex={isMenuOpen ? 0 : -1}
                      css={secondaryItemLinkStyles}
                      to={href}
                      onClick={onDropdownItemClick}
                    >
                      {text}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const dropdownTitleStyles = css({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
});

const iconWrapperStyles = css({
  transition: 'transform 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
});

const iconWrapperRotateStyles = css({
  transform: 'rotate(180deg)',
});

const dropdownStyles = css({
  display: 'flex',
  flexDirection: 'column',
  position: 'absolute',
  top: 40,
  left: 0,
  boxShadow: token('elevation.shadow.overlay'),
  borderRadius: '15px',
  overflow: 'hidden',
  opacity: 0,
  pointerEvents: 'none',
  transform: 'translateY(16px)',
  transition: 'opacity 0.2s cubic-bezier(0.22, 1, 0.36, 1), transform 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
  cursor: 'default',
});

const dropdownOpenStyles = css({
  opacity: 1,
  pointerEvents: 'auto',
  transform: 'translateY(0)',
});

const defaultListStyles = css({
  display: 'flex',
  padding: 0,
  margin: 0,
});

const primaryContainerStyles = css({
  padding: `${token('space.500')} !important`,
  gap: token('space.400'),
  backgroundColor: '#FFFFFF',
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 184px)',
});

const defaultListItemStyles = css({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  maxWidth: '184px',
  width: '100%',
});

const primaryItemStyles = css({
  '&:hover span:first-of-type': {
    color: '#0052CC',
  },
});

const primaryItemLinkStyles = css({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left',
  textDecoration: 'none !important',
});

const primaryItemTitleStyles = css({
  fontFamily: 'Charlie Text, sans-serif',
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  letterSpacing: 0,
  color: '#101214',
  marginBottom: token('space.050'),
});

const primaryItemDescriptionStyles = css({
  fontFamily: 'Charlie Text, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: '0',
  color: token('color.text'),
});

const secondaryContainerStyles = css({
  display: 'grid',
  gridTemplateColumns: '400px 184px 184px',
  textAlign: 'left',
  padding: `${token('space.500')} !important`,
  gap: token('space.400'),
  color: '#101214',
  backgroundColor: '#F8F8F8',
  marginTop: '0 !important',
});

const secondaryItemTitleStyles = css({
  fontFamily: 'Charlie Text, sans-serif',
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: '16px',
  letterSpacing: 0,
  color: '#101214',
  marginBottom: token('space.150'),
  textTransform: 'uppercase',
  display: 'inline-block',
});

const secondaryItemListStyles = css({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  flexWrap: 'wrap',
  rowGap: token('space.200'),
  columnGap: token('space.400'),
  maxHeight: '150px',
});

const secondaryItemLinkWrapperStyles = css({
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
});

const secondaryItemLinkStyles = css({
  textDecoration: 'none !important',
  color: token('color.text'),
  fontFamily: 'Charlie Text, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: 0,
});
